import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Bots-page preferences — small per-browser settings for the My Bots
 * view that don't belong in the per-bot data.
 *
 * `includeDrafts`
 * ---------------
 * Draft bots (`Bot.isDraft`) are unfinished bots saved mid-build. By
 * default they're hidden from the All / Active / Paused filters and
 * only appear under the dedicated "Drafts" filter — so a half-built
 * bot doesn't clutter the user's real portfolio list. This flag is
 * the opt-in escape hatch (Sergey 2026-05-13: "we need an option to
 * enable it"): when `true`, drafts also show up inside the "All"
 * filter. Active / Paused stay draft-free regardless — a draft has
 * no live trading status to match.
 *
 * Persisted to localStorage under `whb-bots-prefs`.
 */
interface BotsPrefsState {
  includeDrafts: boolean;
  setIncludeDrafts: (v: boolean) => void;
  toggleIncludeDrafts: () => void;
}

export const useBotsPrefsStore = create<BotsPrefsState>()(
  persist(
    (set) => ({
      includeDrafts: false,
      setIncludeDrafts: (v) => set({ includeDrafts: v }),
      toggleIncludeDrafts: () =>
        set((s) => ({ includeDrafts: !s.includeDrafts })),
    }),
    {
      name: "whb-bots-prefs",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
